import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  UnitTransferRequestStatusEnumType,
  UnitTransferSignatureDocumentTypeEnumType,
  UnitTransferSignatureStatusEnumType,
  UnitTransferSigningModeEnumType,
} from '__generated__/graphql';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import Badge from 'primitives/Badge';
import ConfirmButton from 'primitives/ConfirmButton';
import LoadingIndicator from 'primitives/LoadingIndicator';
import Switch from 'primitives/Switch';

import ErrorMessage from 'components/ErrorMessage';
import SectionCard from 'components/SectionCard';

import unitTransferSignatureDocumentTypeToReadable from 'utils/enums/unit-transfer-signature-document-type-to-readable';
import unitTransferSignatureStatusToReadable from 'utils/enums/unit-transfer-signature-status-to-readable';

import SignatureRequestCard from './SignatureRequestCard';

const UNIT_TRANSFER_REQUEST_SIGNATURES_QUERY = gql(`
  query getUnitTransferRequestSignatures($id: ID!) {
    unitTransferRequest(id: $id) {
      id
      status
      signingMode
      signatureRequests {
        id
        documentType
        sequence
        status
        signingMode
        externalStatus
        externalDocumentId
        signedFileUrl
        blankTemplateUrl
        sentAt
        completedAt
        createdAt
        signers {
          id
          role
          name
          email
          status
          signedAt
          signingLinkUrl
        }
      }
    }
  }
`);

const INITIATE_UNIT_TRANSFER_SIGNING_MUTATION = gql(`
  mutation InitiateUnitTransferSigning($id: ID!) {
    initiateUnitTransferSigning(id: $id) {
      id
      status
      signatureRequests {
        id
        documentType
        sequence
        status
        signingMode
        signers {
          id
          role
          name
          email
          status
        }
      }
    }
  }
`);

const SET_UNIT_TRANSFER_SIGNING_MODE_MUTATION = gql(`
  mutation SetUnitTransferSigningMode($requestId: ID!, $mode: UnitTransferSigningModeEnumType!) {
    setUnitTransferSigningMode(requestId: $requestId, mode: $mode) {
      id
      signingMode
      signatureRequests {
        id
        signingMode
      }
    }
  }
`);

type SignatureRequestRow = {
  id: string;
  documentType: UnitTransferSignatureDocumentTypeEnumType;
  sequence: number;
  status: UnitTransferSignatureStatusEnumType;
};

// One row per signature request in the side list — mirrors the Documents tab's layout so ops
// navigates both review screens the same way.
const SignatureRequestNavItem: FC<{
  signatureRequest: SignatureRequestRow;
  isBlocked: boolean;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ signatureRequest, isBlocked, isSelected, onSelect }) => {
  const { label, color } = unitTransferSignatureStatusToReadable(signatureRequest.status);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full flex-shrink-0 text-left px-3 py-2.5 rounded-md border transition-colors md:flex-shrink ${
        isSelected
          ? 'bg-indigo-50 border-indigo-200'
          : 'bg-white border-transparent hover:bg-gray-50'
      }`}
    >
      <p className={`text-sm font-medium ${isSelected ? 'text-indigo-900' : 'text-gray-900'}`}>
        {signatureRequest.sequence}.{' '}
        {unitTransferSignatureDocumentTypeToReadable(signatureRequest.documentType)}
      </p>
      <div className="mt-1.5 flex items-center gap-1.5">
        <Badge label={label} color={color} showIndicator />
        {isBlocked && <span className="text-xs font-medium text-amber-700">Waiting</span>}
      </div>
    </button>
  );
};

const UnitTransferSignatures: FC = () => {
  const { unitTransferId } = useParams<{ unitTransferId: string }>() as { unitTransferId: string };
  const [selectedSignatureRequestId, setSelectedSignatureRequestId] = useState<string | null>(null);

  const { loading, error, data, refetch } = useQuery(UNIT_TRANSFER_REQUEST_SIGNATURES_QUERY, {
    variables: { id: unitTransferId },
  });

  const [initiateSigning, { loading: initiating, error: initiateError }] = useMutation(
    INITIATE_UNIT_TRANSFER_SIGNING_MUTATION
  );

  const [setSigningMode, { loading: settingSigningMode, error: signingModeError }] = useMutation(
    SET_UNIT_TRANSFER_SIGNING_MODE_MUTATION
  );

  if (loading && !data) return <LoadingIndicator />;
  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const unitTransferRequest = data.unitTransferRequest;
  const signatureRequests = [...unitTransferRequest.signatureRequests].sort(
    (a, b) => a.sequence - b.sequence
  );

  // Only the next eligible document offers Send — every earlier-sequence request must be SIGNED
  // first (BUS_097). Computed client-side so the UI explains what's outstanding instead of
  // letting the mutation fail.
  const blockedByFor = (signatureRequest: SignatureRequestRow) =>
    signatureRequests
      .filter(
        other =>
          other.sequence < signatureRequest.sequence &&
          other.status !== UnitTransferSignatureStatusEnumType.Signed
      )
      .map(other => unitTransferSignatureDocumentTypeToReadable(other.documentType));

  const selectedSignatureRequest =
    signatureRequests.find(sr => sr.id === selectedSignatureRequestId) ??
    signatureRequests[0] ??
    null;

  return (
    <div className="flex flex-col gap-y-4">
      <SectionCard title="Signing Mode" bodyClassName="px-4 py-3 sm:p-4 flex flex-col gap-3">
        <Switch
          checked={unitTransferRequest.signingMode === UnitTransferSigningModeEnumType.Manual}
          disabled={settingSigningMode}
          label="Manual signing for all not-yet-signed documents"
          helperText="Off routes new documents through e-signing. On skips e-signing — the relevant party downloads a blank template, signs it outside the system, and uploads the signed copy back. Individual documents can still be overridden below regardless of this setting. Already-signed documents are never affected."
          onChange={checked =>
            setSigningMode({
              variables: {
                requestId: unitTransferId,
                mode: checked
                  ? UnitTransferSigningModeEnumType.Manual
                  : UnitTransferSigningModeEnumType.Digital,
              },
            })
          }
        />
        {signingModeError && <ErrorMessage error={signingModeError} type="alert" />}
      </SectionCard>

      {signatureRequests.length === 0 && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-600">
            Signing has not been initiated for this request yet.
          </p>
          {unitTransferRequest.status === UnitTransferRequestStatusEnumType.DocumentsVerified ? (
            <ConfirmButton
              loading={initiating}
              error={initiateError}
              description="Initiate the signing plan for this transfer? This creates every signature request that applies to this transfer."
              onConfirm={() => initiateSigning({ variables: { id: unitTransferId } })}
            >
              Initiate Signing
            </ConfirmButton>
          ) : (
            <p className="text-sm text-gray-500">
              Signing can only be initiated once all documents have been verified.
            </p>
          )}
        </div>
      )}

      {signatureRequests.length > 0 && selectedSignatureRequest && (
        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <div className="flex flex-row gap-2 overflow-x-auto md:w-72 md:flex-shrink-0 md:flex-col md:overflow-visible">
            {signatureRequests.map(signatureRequest => (
              <SignatureRequestNavItem
                key={signatureRequest.id}
                signatureRequest={signatureRequest}
                isBlocked={
                  signatureRequest.status === UnitTransferSignatureStatusEnumType.NotSent &&
                  blockedByFor(signatureRequest).length > 0
                }
                isSelected={selectedSignatureRequest.id === signatureRequest.id}
                onSelect={() => setSelectedSignatureRequestId(signatureRequest.id)}
              />
            ))}
          </div>

          <div className="min-w-0 flex-1">
            <SignatureRequestCard
              signatureRequest={selectedSignatureRequest}
              blockedBy={blockedByFor(selectedSignatureRequest)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitTransferSignatures;
